import { useMemo, useEffect, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  type Node,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import '../../styles/print.css';
import CustomEdge from '../CustomEdge';

// Import existing node components
import { 
  MainSchemaDeviceNode,
  MainSchemaActuatorNode,
  TerminalSchemaDeviceNode,
  MainSchemaTerminalNode
} from '../shared/nodes';
import { getSchemaLayout } from '../../api/schemas';
import { getConnections, getDeviceContacts, getCableToTerminalConnections } from '../../api/connections';
import type { DeviceContact } from '../../types/connections';
import type { ElectricConnection } from '../../types/connections';
import { mapConnectionToEdge, buildAutoTerminalEdges } from '../shared/graph/edgeBuilder';

interface PrintCanvasProps {
  schemaType: 'main' | 'terminal';
  projectId: string;
  schemaId?: number;
  blockId?: number;
  viewport: {
    worldX: number;
    worldY: number;
    worldWidth: number;
    worldHeight: number;
    scale: number;
    screenWidth: number;
    screenHeight: number;
  };
  printMode?: boolean;
  showBackground?: boolean;
  className?: string;
  viewportId?: number;
  onViewportChange?: (viewportId: number, updates: { scroll_x: number; scroll_y: number; zoom: number }) => void;
  globalZoomMode?: boolean;
}

interface SchemaLayoutItem {
  device_instance_id: number;
  specification_id: number;
  position_x: number;
  position_y: number;
  rotation?: number;
  position_label?: string;
  description?: string;
  spec_name?: string;
  device_type?: string;
  device_type_name?: string;
  device_type_code?: string;
  is_reserve?: number;
  properties?: string | null;
}

// Use existing types from the project

// Node types for different schema types
const getNodeTypes = (schemaType: string): NodeTypes => {
  if (schemaType === 'terminal') {
    const mapping: NodeTypes = {
      device: TerminalSchemaDeviceNode,
      cable: TerminalSchemaDeviceNode,
    };
    return mapping;
  }
  const mapping: NodeTypes = {
    device: MainSchemaDeviceNode,
    actuator: MainSchemaActuatorNode,
    cable: MainSchemaDeviceNode,
    autoTerminal: MainSchemaTerminalNode,
  };
  return mapping;
};

// Removed viewport filtering for now - show all elements

function PrintCanvasContent({ 
  schemaType, 
  projectId, 
  schemaId, 
  blockId, 
  viewport, 
  printMode = false,
  showBackground = false,
  className = '',
  viewportId,
  onViewportChange,
}: PrintCanvasProps) {
  
  const [contactsCache, setContactsCache] = useState<Record<number, DeviceContact[]>>({});
  const [restoredViewport, setRestoredViewport] = useState<{ x: number; y: number; zoom: number } | null>(null);

  // Viewport persistence for old architecture
  const getViewportStorageKey = useCallback(() => {
    if (schemaType === 'main' && schemaId) {
      return `print-canvas-viewport-${projectId}-main-${schemaId}`;
    } else if (schemaType === 'terminal' && blockId) {
      return `print-canvas-viewport-${projectId}-terminal-${blockId}`;
    }
    return null;
  }, [projectId, schemaType, schemaId, blockId]);

  // Save viewport to localStorage
  const saveViewportToStorage = useCallback((newViewport: { x: number; y: number; zoom: number }) => {
    const key = getViewportStorageKey();
    if (key) {
      localStorage.setItem(key, JSON.stringify(newViewport));
    }
  }, [getViewportStorageKey]);

  // Restore viewport from localStorage
  const restoreViewportFromStorage = useCallback(() => {
    const key = getViewportStorageKey();
    if (key) {
      const saved = localStorage.getItem(key);
      if (saved) {
        try {
          return JSON.parse(saved) as { x: number; y: number; zoom: number };
        } catch (e) {
          console.error('Error parsing saved viewport:', e);
          return null;
        }
      }
    }
    return null;
  }, [getViewportStorageKey]);

  // Restore viewport from localStorage on mount
  useEffect(() => {
    const savedViewport = restoreViewportFromStorage();
    if (savedViewport) {
      setRestoredViewport(savedViewport);
    }
  }, [restoreViewportFromStorage]);

  // Handle viewport changes and save to database
  const onMove = useCallback((_: any, newViewport: { x: number; y: number; zoom: number }) => {
    if (viewportId && onViewportChange) {
      // Limit coordinates to reasonable bounds
      const limitedX = Math.max(-10000, Math.min(10000, newViewport.x));
      const limitedY = Math.max(-10000, Math.min(10000, newViewport.y));
      const limitedZoom = Math.max(0.1, Math.min(4, newViewport.zoom));
      
      onViewportChange(viewportId, {
        scroll_x: limitedX,
        scroll_y: limitedY,
        zoom: limitedZoom
      });
    }
    
    // Also save to localStorage for old architecture
    saveViewportToStorage(newViewport);
  }, [viewportId, onViewportChange, saveViewportToStorage]);

  // Handle mouse wheel for panning when middle mouse button is held
  const handleWheel = useCallback((event: React.WheelEvent) => {
    // Only handle wheel events when middle mouse button is held (button 1)
    // This will be handled by the parent component for zoom
    event.preventDefault();
  }, []);

  // Handle mouse down for panning with middle mouse button
  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (event.button === 1) { // Middle mouse button
      event.preventDefault();
      event.stopPropagation();
      
      const startX = event.clientX;
      const startY = event.clientY;
      const startViewportX = viewport.worldX;
      const startViewportY = viewport.worldY;
      
      const handleMouseMove = (e: MouseEvent) => {
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;
        
        const newX = startViewportX - deltaX;
        const newY = startViewportY - deltaY;
        
        if (viewportId && onViewportChange) {
          // Limit coordinates to reasonable bounds
          const limitedX = Math.max(-10000, Math.min(10000, newX));
          const limitedY = Math.max(-10000, Math.min(10000, newY));
          const limitedZoom = Math.max(0.1, Math.min(4, viewport.scale));
          
          onViewportChange(viewportId, {
            scroll_x: limitedX,
            scroll_y: limitedY,
            zoom: limitedZoom
          });
        }
      };
      
      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  }, [viewport.worldX, viewport.worldY, viewport.scale, viewportId, onViewportChange]);

  // Load schema layout
  const { data: schemaLayout = [] } = useQuery({
    queryKey: ['print-schema-layout', schemaType, projectId, schemaId, blockId],
    queryFn: async () => {
      if (schemaType === 'main' && schemaId) {
        // Use non-isolated API for stability in PrintCanvas
        const resp = await getSchemaLayout(schemaId);
        return resp;
      } else if (schemaType === 'terminal' && blockId) {
        // For terminal schemas, we need to load terminal block data
        // This is a simplified version - you might need to adjust based on your API
        return [];
      }
      return [];
    },
    enabled: !!(projectId && (schemaId || blockId)),
  });

  // Load cable → terminal connections for auto terminals (main schema only)
  const { data: cableTerminalConnections = [] } = useQuery({
    queryKey: ['print-cable-terminal-connections', schemaType, schemaId],
    queryFn: async () => {
      if (schemaType === 'main' && schemaId) {
        return await getCableToTerminalConnections(schemaId);
      }
      return [];
    },
    enabled: schemaType === 'main' && !!schemaId,
  });

  // Load connections
  const { data: connections = [] } = useQuery({
    queryKey: ['print-connections', schemaType, projectId, schemaId, blockId],
    queryFn: async () => {
      if (schemaType === 'main' && schemaId) {
        return await getConnections(schemaId);
      } else if (schemaType === 'terminal' && blockId) {
        // Load terminal connections
        return [];
      }
      return [];
    },
    enabled: !!(projectId && (schemaId || blockId)),
  });

  // Convert layout to nodes (filtered by viewport)
  const filteredNodes = useMemo(() => {
    // Support both formats: array or ApiResponse<{ data: SchemaLayoutItem[] }>
    const layoutArray: SchemaLayoutItem[] = Array.isArray(schemaLayout)
      ? (schemaLayout as unknown as SchemaLayoutItem[])
      : ((schemaLayout as any)?.data ?? []);

    if (!Array.isArray(layoutArray) || layoutArray.length === 0) return [];

    // Filter elements by viewport bounds (world coordinates)
    const visibleElements = layoutArray.filter((item: SchemaLayoutItem) => {
      const x = item.position_x;
      const y = item.position_y;
      
      // Use fixed viewport bounds based on screen dimensions, not zoom
      const worldX = Number(viewport.worldX);
      const worldY = Number(viewport.worldY);
      const screenWidth = viewport.screenWidth;
      const screenHeight = viewport.screenHeight;
      
      // Check if element is within viewport bounds
      return x >= worldX && 
             x <= worldX + screenWidth &&
             y >= worldY && 
             y <= worldY + screenHeight;
    });

    return visibleElements.map((item: SchemaLayoutItem) => {
      const rawType = (item.device_type || item.device_type_code || item.device_type_name || '').toLowerCase();
      const nodeType = rawType.includes('cable') ? 'cable' : (rawType.includes('actuator') ? 'actuator' : 'device');
      
      const finalX = item.position_x; // keep world coords, pan via ReactFlow viewport
      const finalY = item.position_y;

      return {
        id: item.device_instance_id.toString(),
        type: nodeType as any,
        position: { 
          x: finalX, 
          y: finalY 
        },
        data: {
          deviceInstanceId: item.device_instance_id,
          specificationId: item.specification_id,
          deviceName: item.position_label || '',
          description: item.description,
          deviceType: item.device_type || item.device_type_code || item.device_type_name || 'device',
          specName: item.spec_name || '',
          contacts: contactsCache[item.device_instance_id] || [],
          isReserve: !!item.is_reserve,
          schemaType,
          printMode,
          // Remove interactive elements in print mode
          onRemove: printMode ? undefined : () => {},
          onEdit: printMode ? undefined : () => {},
        },
        draggable: !printMode,
        selectable: !printMode,
      };
    });
  }, [schemaLayout, viewport, contactsCache, printMode]);

  // Build auto terminal nodes from cable → terminal connections
  const autoTerminalNodes = useMemo(() => {
    if (!Array.isArray(cableTerminalConnections) || cableTerminalConnections.length === 0) return [] as Node[];
    // Группируем по кабелю как в UniversalSchemaEditor
    const byCable = new Map<number, any[]>();
    cableTerminalConnections
      .filter((c: any) => c.cable_device_type === 'cable')
      .forEach((c: any) => {
        const id = c.cable_instance_id;
        if (!byCable.has(id)) byCable.set(id, []);
        byCable.get(id)!.push(c);
      });

    const nodes: Node[] = [];
    byCable.forEach((connections: any[]) => {
      if (connections.length === 0) return;
      const cableX = Number(connections[0].cable_position_x) || 100;
      const cableY = Number(connections[0].cable_position_y) || 100;
      // сортируем по порядку жилы
      connections.sort((a: any, b: any) => {
        const aOrder = parseInt(String(a.cable_contact_number).split('-')[0]) || 0;
        const bOrder = parseInt(String(b.cable_contact_number).split('-')[0]) || 0;
        return aOrder - bOrder;
      });
      const cableContactsCount = connections.length;
      const cableWidth = cableContactsCount <= 4 ? 90 : Math.max(90, cableContactsCount * 18 + 20);
      const terminalGroupWidth = (connections.length - 1) * 40;
      const startX = cableX + (cableWidth - terminalGroupWidth) / 2;
      connections.forEach((connection: any, index: number) => {
        const contactSpacing = 40;
        const horizontalOffset = index * contactSpacing;
        const terminalX = startX + horizontalOffset;
        const terminalY = cableY - 160;
        
        // Check if terminal is within viewport bounds
        if (terminalX >= viewport.worldX && 
            terminalX <= viewport.worldX + viewport.worldWidth &&
            terminalY >= viewport.worldY && 
            terminalY <= viewport.worldY + viewport.worldHeight) {
          nodes.push({
            id: `terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`,
            type: 'autoTerminal',
            position: { x: terminalX, y: terminalY },
          data: {
            terminal_instance_id: connection.terminal_instance_id,
            terminal_block_id: connection.terminal_block_id,
            terminal_block_name: connection.terminal_block_name,
            terminal_block_description: connection.terminal_block_description,
            terminal_position_in_block: connection.terminal_position_in_block,
            terminal_contact_id: connection.terminal_contact_id,
            terminal_opposite_contact_id: connection.terminal_opposite_contact_id,
            terminal_contact_label: connection.terminal_contact_label,
            terminal_opposite_contact_label: connection.terminal_opposite_contact_label,
            terminal_specification_id: connection.terminal_specification_id,
            cable_instance_id: connection.cable_instance_id,
            cable_contact_id: connection.cable_contact_id,
            connection_id: connection.connection_id,
            source_schema_id: connection.source_schema_id,
            cable_contact_number: connection.cable_contact_number
          },
          draggable: false,
          selectable: false,
        } as Node);
        }
      });
    });
    return nodes;
  }, [cableTerminalConnections, viewport]);

  const nodesToRender = useMemo(() => {
    return [...filteredNodes, ...autoTerminalNodes];
  }, [filteredNodes, autoTerminalNodes]);

  // Convert connections to edges (filtered by visible nodes)
  const filteredEdges = useMemo(() => {
    if (!Array.isArray(connections) || connections.length === 0) {
      return [];
    }
    // Do not over-filter: map all connections; ReactFlow will skip edges with missing nodes
    return (connections as ElectricConnection[]).map((conn) => mapConnectionToEdge(conn, schemaType));
  }, [connections, schemaType]);

  // Edges for cable → auto terminal connections on all schemas except terminal block schemas
  const autoTerminalEdges = useMemo(() => {
    if (schemaType === 'terminal') return [] as any[];
    if (!Array.isArray(cableTerminalConnections) || cableTerminalConnections.length === 0) return [] as any[];
    // Build edges with normalized data
    return buildAutoTerminalEdges(cableTerminalConnections as any[], nodesToRender);
  }, [schemaType, cableTerminalConnections, nodesToRender]);

  const edgesToRender = useMemo(() => {
    // Force CustomEdge for diagnostics in print mode to render anchor markers
    return [...filteredEdges, ...autoTerminalEdges].map((e: any) => ({ ...e, type: 'custom' }));
  }, [filteredEdges, autoTerminalEdges]);

  // Load contacts for visible devices
  useEffect(() => {
    const loadContacts = async () => {
      const deviceIds = filteredNodes
        .map((node: Node) => parseInt(node.data.deviceInstanceId as string))
        .filter((id: number) => !contactsCache[id]);

      if (deviceIds.length === 0) return;

      const newContacts: Record<number, DeviceContact[]> = {};
      
      for (const deviceId of deviceIds) {
        try {
          const contacts = await getDeviceContacts(deviceId);
          newContacts[deviceId] = contacts;
        } catch (error) {
          console.error(`Failed to load contacts for device ${deviceId}:`, error);
          newContacts[deviceId] = [];
        }
      }

      setContactsCache(prev => ({ ...prev, ...newContacts }));
    };

    loadContacts();
  }, [filteredNodes, contactsCache]);


  const nodeTypes = getNodeTypes(schemaType);
  const edgeTypes = useMemo(() => ({ custom: CustomEdge }), []);

  return (
    <div className={`w-full h-full ${className}`}>
      <style>{`
        /* Print mode styles */
        ${printMode ? `
          .react-flow__controls,
          .react-flow__minimap,
          .react-flow__attribution {
            display: none !important;
          }
          
          .react-flow__node {
            cursor: default !important;
          }
          
          .react-flow__edge {
            pointer-events: none !important;
          }
          
          /* Keep handles mounted but invisible so edges can compute positions */
          .react-flow__handle {
            opacity: 0 !important;
            width: 1px !important;
            height: 1px !important;
            background: transparent !important;
            border: 0 !important;
            pointer-events: none !important;
          }
        ` : ''}
        
        /* Clean print styles */
        .print-canvas {
          background: white !important;
        }
        
        .print-canvas .react-flow__background {
          ${!showBackground ? 'display: none !important;' : ''}
        }
      `}</style>
      
      
      <ReactFlow
        nodes={nodesToRender}
        edges={edgesToRender}
        onNodesChange={() => {}} // Disabled for print mode
        onEdgesChange={() => {}} // Disabled for print mode
        onMove={onMove} // Save viewport position to database
        onWheel={handleWheel} // Handle wheel events
        onMouseDown={handleMouseDown} // Handle mouse down for panning
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView={false}
        minZoom={0.01}
        maxZoom={4}
        defaultViewport={restoredViewport || { x: -Number(viewport.worldX), y: -Number(viewport.worldY), zoom: Number(viewport.scale) }}
        zoomOnScroll={false}
        panOnScroll={false}
        zoomOnDoubleClick={false}
        panOnDrag={false}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        className={`${printMode ? 'print-canvas' : ''} h-full w-full`}
      >
        {showBackground && !printMode && (
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
        )}
      </ReactFlow>
    </div>
  );
}

// Main component with ReactFlowProvider
export default function PrintCanvas(props: PrintCanvasProps) {
  return (
    <ReactFlowProvider>
      <PrintCanvasContent {...props} />
    </ReactFlowProvider>
  );
}
